import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  ListItem,
  List,
  ListItemText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Grid,
  Collapse,
  Tooltip,
  TableFooter,
} from "@mui/material";
import {
  Search,
  Close,
  Person,
  Payment,
  Receipt,
  ArrowDownward,
  ArrowUpward,
  AccountBalance,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
  ShoppingCart,
  CreditCard,
} from "@mui/icons-material";
import { formatCurrencyN } from "../../../common/utils_t";
import {
  createCustomerPayment,
  getCustomerTransactions,
  searchTransactionsByCode,
} from "../../../api/customers/customers";
import { IonApp, IonButton, IonContent, IonPage, useIonViewWillEnter } from "@ionic/react";
import ReceiptModal from '../../../components/sales/ReceiptModal';
import { getBusinessData } from "../../../common/utils";
import ReceiptModal_Large from "../../../components/sales/ReceiptModal_large";
import ReceiptModal_giftcard_crypto from "../../../components/sales/ReceiptModal_giftcard_crypto";
import { MenuButton } from "../../../components/Menu";

// Updated interfaces to match your API response
interface OrderItem {
  product_id: string;
  product_name: string;
  card_amount: string;
  card_rate: string;
  naira_rate: string;
  rmbValue: string;
  load_type: string;
  quantity?: string;
  price?: string;
  total_amount?: string;
  item_status?: string;
}

interface Transaction {
  order_id: string;
  order_code: string;
  order_date: string;
  order_status: "COMPLETED" | "PENDING" | "CANCELLED";
  payment_status: '0' | '1' | '2';
  total_price: string;
  payable_amount: string;
  amount_paid: string;
  balance_due: string;
  member_id: string;
  member_name: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  mode_of_payment: string;
  order_by: string;
  otp_status: string | null;
  items: OrderItem[];
  processor_type: string;
  external_processor: string;
  customer_account_name: string;
  customer_account_number: string;
  customer_bank_code: string;
  bank_name: string;
  product_name: string;
  product_type: string;
  commission: string;
  costPrice: string;
  sellingPrice: string;
  proof_of_payment: string;
  giftcardType?: string;
  product_face_code?: string;
  cryptoType?: string;
  crypto_value?: string;
  note1?: string;
  note2?: string;
  note3?: string;
}

interface Customer {
  id: string;
  user_id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  grade_letter: string;
}

interface SearchResponse {
  status: boolean;
  message: string;
  data: {
    orders: Array<{
      order: Transaction;
      items: Array<{
        order: Transaction;
      }>;
    }>;
    balance: number;
  };
}

const GiftCardTransactionSearch: React.FC = () => {
  const location = useLocation<{ customer?: Customer }>();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchedTransactions, setSearchedTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState({
    transactions: false,
    payment: false,
    search: false,
  });

  // Payment dialog states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState<string[]>([]);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  // Helper function to transform API response to Transaction format
  const transformApiOrderToTransaction = (apiOrder: any): Transaction => {
    const orderData = apiOrder.order || apiOrder;
    
    // Create items array from the order data
    const items: OrderItem[] = [{
      product_id: orderData.product_id_single || orderData.product_id,
      product_name: orderData.product_name,
      card_amount: orderData.card_amount,
      card_rate: orderData.card_rate,
      naira_rate: orderData.naira_rate,
      rmbValue: orderData.rmbValue,
      load_type: orderData.load_type,
      quantity: "1",
      price: orderData.card_amount,
      total_amount: orderData.total_price,
      item_status: orderData.active_status
    }];

    return {
      order_id: orderData.order_id,
      order_code: orderData.order_code,
      order_date: orderData.order_date,
      order_status: orderData.active_status,
      payment_status: orderData.payment_status?.toString() as '0' | '1' | '2',
      total_price: orderData.total_price,
      payable_amount: orderData.payable_amount,
      amount_paid: orderData.amount_paid,
      balance_due: orderData.balance_due,
      member_id: orderData.member_id,
      member_name: orderData.member_name || orderData.customer_name,
      customer_name: orderData.customer_name,
      customer_email: orderData.customer_email,
      customer_phone: orderData.customer_phone,
      mode_of_payment: orderData.mode_of_payment,
      order_by: orderData.order_by,
      otp_status: orderData.otp_status,
      items: items,
      processor_type: orderData.processor_type,
      external_processor: orderData.external_processor,
      customer_account_name: orderData.customer_account_name,
      customer_account_number: orderData.customer_account_number,
      customer_bank_code: orderData.customer_bank_code,
      bank_name: orderData.bank_name,
      product_name: orderData.product_name,
      product_type: orderData.product_id_type,
      commission: orderData.commission,
      costPrice: orderData.costPrice,
      sellingPrice: orderData.sellingPrice,
      proof_of_payment: orderData.proof_of_payment,
      giftcardType: orderData.giftcardType,
      product_face_code: orderData.product_face_code,
      cryptoType: orderData.cryptoType,
      crypto_value: orderData.crypto_value,
      note1: orderData.note1,
      note2: orderData.note2,
      note3: orderData.note3
    };
  };

  // Calculate totals based on actual data structure
  const unpaidTransactions = transactions.filter(
    (txn) => txn.payment_status === '0' || txn.payment_status === "2"
  );

  const selectedTransactionTotal = unpaidTransactions
    .filter((txn) => selectedTransactions.includes(txn.order_id))
    .reduce((sum, txn) => {
      const balanceDue = parseFloat(txn.balance_due) || 0;
      return sum + balanceDue;
    }, 0);

  const totalOutstanding = unpaidTransactions.reduce((sum, txn) => {
    const balanceDue = parseFloat(txn.balance_due) || 0;
    return sum + balanceDue;
  }, 0);

  const getPaymentStatusLabel = (status: string | number) => {
    const code = Number(status);
    switch (code) {
      case 0:
        return "Unpaid";
      case 1:
        return "Paid";
      case 2:
        return "Partial";
      default:
        return "Unknown";
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case "0":
        return "error";
      case "1":
        return "success";
      case "2":
        return "warning";
      default:
        return "default";
    }
  };

  const getOrderStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "PENDING":
        return "warning";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const toggleRowExpansion = (orderId: string) => {
    setExpandedRows((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Search transactions by gift card or crypto code
  const handleSearchChange = async (searchTerm: string) => {
    if (searchTerm.length < 3) {
      setSearchedTransactions([]);
      return;
    }
    
    try {
      setLoading((prev) => ({ ...prev, search: true }));
      const response: SearchResponse = await searchTransactionsByCode(searchTerm);

      console.log("Search response:", response);
      
      if (response.status && response.data.orders) {
        // Transform API response to Transaction format
        const transformedTransactions = response.data.orders.map(transformApiOrderToTransaction);
        setSearchedTransactions(transformedTransactions);
        setBalance(response.data.balance || 0);
      } else {
        setSearchedTransactions([]);
      }
    } catch (error) {
      console.error("Error searching transactions:", error);
      setSearchedTransactions([]);
    } finally {
      setLoading((prev) => ({ ...prev, search: false }));
    }
  };

  // Handle transaction selection from search results
  const handleTransactionSelect = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setTransactions([transaction]); // Set as single transaction in the table
    setSearchQuery("");
    setSearchedTransactions([]);
    setSelectedTransactions([]);
    setPaymentAmount(0);
    setExpandedRows([]);
  };

  const handleTransactionDeselect = () => {
    setSelectedTransaction(null);
    setTransactions([]);
    setSelectedTransactions([]);
    setPaymentAmount(0);
    setExpandedRows([]);
    setBalance(0);
  };

  const handleTransactionSelectCheckbox = (transactionId: string) => {
    setSelectedTransactions((prev) => {
      const newSelected = prev.includes(transactionId)
        ? prev.filter((id) => id !== transactionId)
        : [...prev, transactionId];

      // Update payment amount to match selected transactions
      const newTotal = unpaidTransactions
        .filter((txn) => newSelected.includes(txn.order_id))
        .reduce(
          (sum, txn) => sum + (parseFloat(txn.balance_due) || 0),
          0
        );

      setPaymentAmount(newTotal);
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedTransactions([]);
      setPaymentAmount(0);
    } else {
      const allUnpaidIds = unpaidTransactions.map((txn) => txn.order_id);
      setSelectedTransactions(allUnpaidIds);
      setPaymentAmount(totalOutstanding);
    }
    setSelectAll(!selectAll);
  };

  const handlePaymentAmountChange = (value: number) => {
    setPaymentAmount(Math.abs(value) || 0);
  };

  const getPaymentType = () => {
    if (paymentAmount === selectedTransactionTotal) return "exact";
    if (paymentAmount > selectedTransactionTotal) return "overpayment";
    if (paymentAmount < selectedTransactionTotal) return "partial";
    return "exact";
  };

  const handlePaymentSubmit = async () => {
    if (!selectedTransaction || selectedTransactions.length === 0 || paymentAmount <= 0) return;

    try {
      setLoading((prev) => ({ ...prev, payment: true }));
      const paymentType = getPaymentType();

      const data = {
        customer_id: selectedTransaction.member_id,
        order_ids: selectedTransactions,
        amount: paymentAmount,
        payment_method: paymentMethod,
        payment_type: paymentType,
        notes: paymentNotes || `Payment for order ${selectedTransaction.order_code}`,
      };

      const response = await createCustomerPayment(data);
      
      if (response.status !== true) {
        console.error("Payment creation failed:", response.message);
        return;
      }

      // Generate receipt with payment details
      handleGenerateReceipt(data);
      
      // Refresh the transaction data
      if (searchQuery) {
        await handleSearchChange(searchQuery);
      }

      // Reset payment dialog
      setShowPaymentDialog(false);
      setSelectedTransactions([]);
      setPaymentAmount(0);
      setPaymentNotes("");
      setSelectAll(false);
    } catch (error) {
      console.error("Error creating payment:", error);
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchedTransactions([]);
      return;
    }
    const timerId = setTimeout(() => {
      handleSearchChange(searchQuery);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchQuery]);

  useEffect(() => {
    setSelectAll(
      selectedTransactions.length === unpaidTransactions.length &&
        unpaidTransactions.length > 0
    );
  }, [selectedTransactions, unpaidTransactions]);

  // Receipt functionality
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);

  const handleGenerateReceipt = (paymentData: any) => {
    if (!selectedTransaction) return;

    const paidTransactions = transactions.filter(txn => 
      paymentData.order_ids.includes(txn.order_id)
    );

    const receiptItems = paidTransactions.flatMap(txn => 
      txn.items.map((item, index) => ({
        orderID: `Item ${index + 1}`,
        productName: item.product_name || 'Unknown Product',
        quantity: item.quantity || '1',
        description: `${item.load_type} - ${item.card_amount} at rate ${item.naira_rate}`,
        unitPrice: parseFloat(item.card_rate) || 0,
        amount: parseFloat(item.total_amount || txn.total_price) || 0,
        status: txn.order_status
      }))
    );

    const totalAmount = paidTransactions.reduce((sum, txn) => {
      return sum + parseFloat(txn.total_price);
    }, 0);

    const paidAmount = paymentData.amount;
    const balanceDue = Math.max(0, totalAmount - paidAmount);

    setReceiptData({
      customer: {
        name: selectedTransaction.customer_name || 'Customer',
        phone: selectedTransaction.customer_phone || 'N/A',
        email: selectedTransaction.customer_email || 'N/A'
      },
      order: {
        id: selectedTransaction.order_code,
        date: selectedTransaction.order_date,
        status: selectedTransaction.order_status,
        paymentStatus: '1',
        paymentMethod: paymentData.payment_method || 'cash',
        giftCardCode: selectedTransaction.product_face_code,
        cryptoCardCode: selectedTransaction.crypto_value
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue
      },
      payment: {
        amount: paidAmount,
        method: paymentData.payment_method || 'cash',
        date: new Date().toISOString(),
        notes: paymentData.notes || `Payment for order ${selectedTransaction.order_code}`
      }
    });
    setReceiptOpen(true);
  };

  const handlePrintReceipt = (transaction: Transaction) => {
    const receiptItems = transaction.items.map((item, index) => ({
      orderID: `Item ${index + 1}`,
      productName: item.product_name,
      quantity: item.quantity || "1",
      unitPrice: parseFloat(item.card_rate) || 0,
      amount: parseFloat(transaction.total_price),
      description: `${item.load_type} - ${item.card_amount} at rate ${item.naira_rate}`,
      status: transaction.order_status
    }));

    const totalAmount = parseFloat(transaction.total_price);
    const paidAmount = parseFloat(transaction.amount_paid);
    const balanceDue = parseFloat(transaction.balance_due);

    setReceiptData({
      customer: {
        name: transaction.customer_name || 'Customer',
        phone: transaction.customer_phone || 'N/A',
        email: transaction.customer_email || 'N/A'
      },
      order: {
        id: transaction.order_code,
        date: transaction.order_date,
        status: transaction.order_status,
        paymentStatus: getPaymentStatusLabel(transaction.payment_status),
        paymentMethod: transaction.mode_of_payment || 'cash',
        giftCardCode: transaction.product_face_code,
        cryptoCardCode: transaction.crypto_value,
        cardValue:transaction.cardValue
      },
      items: receiptItems,
      summary: {
        totalAmount,
        paidAmount,
        balanceDue
      }
    });
    setReceiptOpen(true);
  };

  // Calculate totals for display
  const totalPayableAmount = transactions.reduce(
    (sum, txn) => sum + parseFloat(txn.payable_amount || txn.total_price || '0'),
    0
  );

  const totalBalanceDue = transactions.reduce(
    (sum, txn) => sum + parseFloat(txn.balance_due || '0'),
    0
  );

  return (
    <IonPage className="pushDown">
                <MenuButton/>
      
      <IonContent>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom>
            Search Transactions By Gift/Crypto Card Code
          </Typography>

          {/* Transaction Code Search */}
          <Box sx={{ mb: 4 }}>
            <TextField
              fullWidth
              placeholder="Search by Gift Card Code, Crypto Card Code, Order Code, or Customer Name..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditCard />
                  </InputAdornment>
                ),
                endAdornment: selectedTransaction && (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={handleTransactionDeselect}
                    >
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={!!selectedTransaction}
            />
            
            {loading.search && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {searchedTransactions.length > 0 && (
              <Paper sx={{ mt: 1, maxHeight: '300px', overflow: "auto" }}>
                <List>
                  {searchedTransactions.map((transaction, index) => (
                    <ListItem
                      button
                      key={transaction.order_id}
                      onClick={() => handleTransactionSelect(transaction)}
                    >
                      <ListItemText
                        primary={`Order: ${transaction.order_code}`}
                        secondary={
                          <Box>
                            <div>
                              <strong>Customer:</strong> {transaction.customer_name}
                            </div>
                            <div>
                              <strong>Product:</strong> {transaction.product_name}
                            </div>
                            <div>
                              <strong>Amount:</strong> {formatCurrencyN(parseFloat(transaction.total_price))}
                            </div>
                            {transaction.product_face_code && (
                              <div>
                                <strong>Card Code:</strong> {transaction.product_face_code}
                              </div>
                            )}
                            {transaction.crypto_value && (
                              <div>
                                <strong>Crypto Value:</strong> {transaction.crypto_value}
                              </div>
                            )}
                            <div>
                              <strong>Status:</strong> {getPaymentStatusLabel(transaction.payment_status)}
                            </div>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Box>

          {/* Transaction Details Section */}
          {selectedTransaction && (
            <Box>
              {/* Transaction Info Header */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
                        <CreditCard fontSize="large" />
                      </Avatar>
                      <Box>
                        <Typography variant="h5">
                          Order: {selectedTransaction.order_code}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Customer: {selectedTransaction.customer_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Date: {selectedTransaction.order_date}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Product: {selectedTransaction.product_name}
                        </Typography>
                        {(selectedTransaction.product_face_code || selectedTransaction.crypto_value) && (
                          <Box sx={{ mt: 1 }}>
                            {selectedTransaction.product_face_code && (
                              <Chip 
                                label={`Card Code: ${selectedTransaction.product_face_code}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mr: 1 }}
                              />
                            )}
                            {selectedTransaction.crypto_value && (
                              <Chip 
                                label={`Crypto: ${selectedTransaction.crypto_value}`}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <IconButton onClick={handleTransactionDeselect}>
                      <Close />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              {/* Payment Summary */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ bgcolor: "info.light" }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Receipt sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Total Amount</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(parseFloat(selectedTransaction.total_price))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ 
                    bgcolor: parseFloat(selectedTransaction.balance_due) > 0 
                      ? "warning.light" 
                      : "success.light" 
                  }}>
                    <CardContent sx={{ textAlign: "center" }}>
                      <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Balance Due</Typography>
                      <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                        {formatCurrencyN(parseFloat(selectedTransaction.balance_due))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Payment sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h6">Payment Status</Typography>
                      <Chip
                        label={getPaymentStatusLabel(selectedTransaction.payment_status)}
                        color={getPaymentStatusColor(selectedTransaction.payment_status) as any}
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Additional Transaction Details */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Transaction Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Customer Email:</strong> {selectedTransaction.customer_email}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Customer Phone:</strong> {selectedTransaction.customer_phone}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Payment Method:</strong> {selectedTransaction.mode_of_payment}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Processor:</strong> {selectedTransaction.external_processor}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Bank:</strong> {selectedTransaction.bank_name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Account Name:</strong> {selectedTransaction.customer_account_name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Account Number:</strong> {selectedTransaction.customer_account_number}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Load Type:</strong> {selectedTransaction.items[0]?.load_type}
                      </Typography>
                    </Grid>
                  </Grid>
                  {selectedTransaction.note1 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Notes:</strong> {selectedTransaction.note1}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Payment Actions */}
              {(selectedTransaction.payment_status === '0' || selectedTransaction.payment_status === '2') && (
                <Box sx={{ mb: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Payment />}
                    onClick={() => {
                      setSelectedTransactions([selectedTransaction.order_id]);
                      setPaymentAmount(parseFloat(selectedTransaction.balance_due));
                      setShowPaymentDialog(true);
                    }}
                  >
                    Record Payment
                  </Button>
                </Box>
              )}

              {/* Order Details Table */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                Order Details
              </Typography>

              {loading.transactions ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : transactions.length > 0 ? (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Code</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Card Amount</TableCell>
                        <TableCell align="right">Rate</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                        <TableCell align="right">Balance Due</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Order Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((txn) => {
                        const isExpanded = expandedRows.includes(txn.order_id);
                        const mainItem = txn.items[0];

                        return (
                          <React.Fragment key={txn.order_id}>
                            <TableRow
                              sx={{
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {txn.order_code}
                                </Typography>
                              </TableCell>
                              <TableCell>{txn.order_date}</TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                    {txn.product_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {mainItem?.load_type} • {txn.external_processor}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {mainItem?.card_amount} {mainItem?.card_rate ? `at ${mainItem.card_rate}` : ''}
                              </TableCell>
                              <TableCell align="right">
                                {mainItem?.naira_rate}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ fontWeight: "bold" }}
                              >
                                {formatCurrencyN(parseFloat(txn.total_price))}
                              </TableCell>
                              <TableCell align="right" sx={{
                                color: parseFloat(txn.balance_due) > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}>
                                {formatCurrencyN(parseFloat(txn.balance_due))}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getPaymentStatusLabel(txn.payment_status)}
                                  size="small"
                                  color={getPaymentStatusColor(txn.payment_status) as any}
                                  icon={Number(txn.payment_status) === 1 ? <CheckCircle fontSize="small" /> : undefined}
                                />
                                {/* {Number(txn.payment_status) === 1 && (
                                  <Tooltip title="Print receipt">
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handlePrintReceipt(txn)}
                                      color="primary"
                                      sx={{ ml: 1 }}
                                    >
                                      <Receipt fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )} */}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={txn.order_status}
                                  size="small"
                                  color={getOrderStatusColor(txn.order_status) as any}
                                />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View transaction details">
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(txn.order_id)}
                                  >
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>

                            {/* Expandable row for transaction details */}
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={10}
                              >
                                <Collapse
                                  in={isExpanded}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box sx={{ margin: 1, p: 2, bgcolor: 'background.default' }}>
                                    <Typography variant="h6" gutterBottom>
                                      Transaction Details
                                    </Typography>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="body2">
                                          <strong>Customer:</strong> {txn.customer_name}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Email:</strong> {txn.customer_email}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Phone:</strong> {txn.customer_phone}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Payment Method:</strong> {txn.mode_of_payment}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Bank:</strong> {txn.bank_name}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <Typography variant="body2">
                                          <strong>Account Name:</strong> {txn.customer_account_name}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Account Number:</strong> {txn.customer_account_number}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Commission:</strong> {formatCurrencyN(parseFloat(txn.commission))}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Cost Price:</strong> {formatCurrencyN(parseFloat(txn.costPrice))}
                                        </Typography>
                                        <Typography variant="body2">
                                          <strong>Selling Price:</strong> {formatCurrencyN(parseFloat(txn.sellingPrice))}
                                        </Typography>
                                      </Grid>
                                    </Grid>
                                    {txn.note1 && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                          <strong>Notes:</strong> {txn.note1}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    No transaction details available
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {/* Payment Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              Record Payment for Order {selectedTransaction?.order_code}
              <IconButton
                sx={{ position: "absolute", right: 8, top: 8 }}
                onClick={() => setShowPaymentDialog(false)}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" gutterBottom>
                    Customer: {selectedTransaction?.customer_name}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Order Code: {selectedTransaction?.order_code}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Product: {selectedTransaction?.product_name}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    Total Amount Due:{" "}
                    <strong>{formatCurrencyN(selectedTransactionTotal)}</strong>
                  </Typography>

                  {/* Card Code Information */}
                  {(selectedTransaction?.product_face_code || selectedTransaction?.crypto_value) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Transaction Information:
                      </Typography>
                      {selectedTransaction?.product_face_code && (
                        <Typography variant="body2">
                          Card Code: {selectedTransaction.product_face_code}
                        </Typography>
                      )}
                      {selectedTransaction?.crypto_value && (
                        <Typography variant="body2">
                          Crypto Value: {selectedTransaction.crypto_value}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Payment Type Alert */}
                  {paymentAmount > 0 && (
                    <Alert
                      severity={
                        getPaymentType() === "exact"
                          ? "success"
                          : getPaymentType() === "overpayment"
                          ? "info"
                          : "warning"
                      }
                      sx={{ mt: 2 }}
                    >
                      {getPaymentType() === "exact" &&
                        "Exact payment - order will be marked as paid"}
                      {getPaymentType() === "overpayment" &&
                        `Overpayment of ${formatCurrencyN(
                          paymentAmount - selectedTransactionTotal
                        )} - excess will be added to account credit`}
                      {getPaymentType() === "partial" &&
                        `Partial payment - ${formatCurrencyN(
                          selectedTransactionTotal - paymentAmount
                        )} will remain outstanding`}
                    </Alert>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) =>
                      handlePaymentAmountChange(parseFloat(e.target.value))
                    }
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₦</InputAdornment>
                      ),
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setPaymentAmount(selectedTransactionTotal)}
                    >
                      Exact Amount
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setPaymentAmount(selectedTransactionTotal * 0.5)
                      }
                    >
                      50%
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setPaymentAmount(selectedTransactionTotal * 0.25)
                      }
                    >
                      25%
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    select
                    label="Payment Method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="card">POS</MenuItem>
                    <MenuItem value="transfer">Bank Transfer</MenuItem>
                    <MenuItem value="wallet">Wallet</MenuItem>
                  </TextField>

                  <TextField
                    fullWidth
                    label="Payment Notes"
                    multiline
                    rows={3}
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="Optional payment notes..."
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowPaymentDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handlePaymentSubmit}
                disabled={
                  !paymentAmount ||
                  selectedTransactions.length === 0 ||
                  loading.payment
                }
                startIcon={
                  loading.payment ? <CircularProgress size={20} /> : <Receipt />
                }
              >
                Record Payment ({formatCurrencyN(paymentAmount)})
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>

      {/* Receipt Modal */}
      {getBusinessData() && getBusinessData().receipt_layout === 'large' && (
        receiptData && (
          <ReceiptModal_giftcard_crypto
            open={receiptOpen}
            onClose={() => setReceiptOpen(false)}
            receiptData={receiptData}
          />
        )
      )  }
      
    </IonPage>
  );
};

export default GiftCardTransactionSearch;